import { ArrowCircleDownIcon, TrashIcon } from '@phosphor-icons/react';
import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';

import { Button } from '../../../../components/button/Button';
import { ProductTypeComboboxField } from '../../productType/ProductTypeComboboxField';
import { InputField } from '../../../../components/input/InputField';
import { VatRateComboboxField } from '../../vatRate/VatRateComboboxField';
import { IOrderPurchaseValues } from '../orderPurchase/CreateOrderPurchase';
import { useMinimalGeneralSettings } from '../../../../contexts/minimal-settings-context';
import { decimateAndFormatNumber, parseNumberInput } from '../../../../utils/number';
import { ICreateOrderLineFieldsProps } from './CreateOrderLineFields';
import {
  calculateDieselSurchargeFromLine,
  findDieselSurchargeBaseLine,
  getEffectiveDieselSurchargePercentage,
  isDieselSurchargeProductType,
} from '../../../../domain/diesel-surcharge';

export interface ICreateOrderLinePurchaseProps {
  index: number;
  onRemove: (index: number) => void;
  purchases: Array<IOrderPurchaseValues>;
  supplier: ICreateOrderLineFieldsProps['supplier'];
}

export const CreateOrderLinePurchase: React.FC<ICreateOrderLinePurchaseProps> = (props) => {
  const { index, onRemove, purchases, supplier } = props;
  const formikbag = useFormikContext();
  const { settings } = useMinimalGeneralSettings();

  const dieselSurchargePercentage = getEffectiveDieselSurchargePercentage(supplier, settings.dieselSurchargePercentage);
  const productType = purchases[index].productType;
  const isDieselSurchargeSelected = isDieselSurchargeProductType(
    productType?.key,
    settings.dieselSurchargeProductType?.id,
  );

  useEffect(() => {
    if (!isDieselSurchargeSelected) {
      return;
    }
    const basePurchase = findDieselSurchargeBaseLine(
      purchases
        .map((purchase) => ({
          productTypeId: purchase.productType?.key ?? '',
          amount: purchase.amount ? parseNumberInput(purchase.amount, 2) : 0,
          unitPrice: purchase.unitPrice ? parseNumberInput(purchase.unitPrice, 2) : 0,
        }))
        .filter((purchase) => purchase.productTypeId),
      {
        baseProductTypeId: settings.dieselSurchargeBaseProductType?.id,
        defaultProductTypeId: settings.defaultProductType?.id,
        dieselProductTypeId: settings.dieselSurchargeProductType?.id,
      },
    );
    if (!basePurchase) {
      return;
    }
    const surchargeValue = calculateDieselSurchargeFromLine(basePurchase, dieselSurchargePercentage);
    formikbag.setFieldValue(`purchases[${index}].amount`, '1');
    formikbag.setFieldValue(`purchases[${index}].unitPrice`, decimateAndFormatNumber(surchargeValue, 2));
  }, [productType?.key]);

  return (
    <div className="card p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="flex gap-2 items-center">
          <ArrowCircleDownIcon className="w-6 h-6 text-negative-text" />
          <div className="heading-three">{`Aankoop ${index + 1}`}</div>
        </h3>
        <Button
          color="danger"
          onTrigger={() => {
            onRemove(index);
          }}
          iconLeft={<TrashIcon className="button-icon" />}
        >
          Verwijder
        </Button>
      </div>

      <ProductTypeComboboxField labelText="Product" name={`purchases[${index}].productType`} />

      {isDieselSurchargeSelected && (
        <div className="mb-4 text-sm">
          {`Diesel toeslag voor vervoerder is ${decimateAndFormatNumber(dieselSurchargePercentage, 2)}%`}
        </div>
      )}

      <div className="flex gap-2">
        <div className="flex-2">
          <InputField labelText="Aantal" type="number" name={`purchases[${index}].amount`} step="0.01" />
        </div>
        <div className="flex-2">
          <InputField labelText="Eenheidsprijs" type="number" name={`purchases[${index}].unitPrice`} step="0.01" />
        </div>
        <div className="flex-3">
          <VatRateComboboxField labelText="Btw Tarief" name={`purchases[${index}].vatRate`} />
        </div>
      </div>
      <InputField labelText="Extra info" type="text" name={`purchases[${index}].externalNote`} />
    </div>
  );
};
